import type { DateTime } from 'luxon';
import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from '@/ui';
import { usePickerYear } from '../../hooks/usePickerYear';
import type { DateRangePickerProps } from '../../types';
import { getRangeBounds, getRangeForAnchor } from './dateRange';

const formatMonth = (month: DateTime): string => month.toFormat('MMMM yyyy');
const formatMonthShort = (month: DateTime): string =>
  month.toFormat('MMM yyyy');

export const DateRangePicker = ({
  experience,
  value,
  onRangeChange,
  compact = false,
}: DateRangePickerProps) => {
  const { start, end } = getRangeBounds(experience);
  const showYears = end.diff(start, 'months').months > 18;

  const months: DateTime[] = [];
  let cursor = start.startOf('month');
  const lastMonth = end.startOf('month');
  while (cursor <= lastMonth) {
    months.push(cursor);
    cursor = cursor.plus({ months: 1 });
  }

  const startYear = start.year;
  const endYear = end.year;

  const years: number[] = [];
  for (let y = startYear; y <= endYear; y += 1) {
    years.push(y);
  }

  const [selectedYear, selectYear] = usePickerYear({
    experience,
    value,
    startYear,
    endYear,
  });

  const visibleMonths = showYears
    ? months.filter((m) => m.year === selectedYear)
    : months;

  const isSelected = (month: DateTime): boolean =>
    Boolean(value && value.startDate.hasSame(month, 'month'));

  const handleSelect = (month: DateTime) => {
    onRangeChange(getRangeForAnchor(month));
  };

  const currentIndex = value
    ? months.findIndex((m) => value.startDate.hasSame(m, 'month'))
    : -1;
  const prevMonth = currentIndex > 0 ? months[currentIndex - 1] : undefined;
  const nextMonth =
    currentIndex >= 0 && currentIndex < months.length - 1
      ? months[currentIndex + 1]
      : undefined;

  const buttonLabel = value
    ? (compact ? formatMonthShort : formatMonth)(value.startDate)
    : 'Select month';

  return (
    <Flex align="center">
      <IconButton
        aria-label="Previous month"
        icon={<Icon icon="ChevronLeft" />}
        isDisabled={!prevMonth}
        size="sm"
        variant="ghost"
        onClick={() => prevMonth && handleSelect(prevMonth)}
      />
      <Menu isLazy placement="bottom">
        <MenuButton
          as={Button}
          paddingInlineEnd={{ base: 1, sm: undefined }}
          rightIcon={<Icon icon="ChevronDown" />}
          size="sm"
          variant="control"
          w={compact ? '104px' : { base: '135px', sm: '150px' }}
        >
          {buttonLabel}
        </MenuButton>
        <MenuList
          maxH={showYears ? undefined : '60vh'}
          overflowY={showYears ? undefined : 'auto'}
        >
          {showYears ? (
            <Flex>
              <Box flex="1" maxH="60vh" overflowY="auto">
                {visibleMonths.map((month) => (
                  <MenuItem
                    fontWeight={isSelected(month) ? 600 : 400}
                    key={month.toISO()}
                    onClick={() => handleSelect(month)}
                  >
                    {formatMonth(month)}
                  </MenuItem>
                ))}
              </Box>
              <Box
                borderColor="gray.200"
                borderLeftStyle="solid"
                borderLeftWidth="1px"
                flex="0 0 auto"
                maxH="60vh"
                overflowY="auto"
              >
                {years.map((year) => (
                  <MenuItem
                    closeOnSelect={false}
                    fontWeight={year === selectedYear ? 600 : 400}
                    key={year}
                    onClick={() => selectYear(year)}
                  >
                    {year}
                  </MenuItem>
                ))}
              </Box>
            </Flex>
          ) : (
            months.map((month) => (
              <MenuItem
                fontWeight={isSelected(month) ? 600 : 400}
                key={month.toISO()}
                onClick={() => handleSelect(month)}
              >
                {formatMonth(month)}
              </MenuItem>
            ))
          )}
        </MenuList>
      </Menu>
      <IconButton
        aria-label="Next month"
        icon={<Icon icon="ChevronRight" />}
        isDisabled={!nextMonth}
        size="sm"
        variant="ghost"
        onClick={() => nextMonth && handleSelect(nextMonth)}
      />
    </Flex>
  );
};
